/**
 * EN / ES translations for all form pages.
 * Imported and merged into the main translations object.
 */
export const formTranslations = {
  en: {
    forms: {
      backHome: "Back to Home",
      required: "This field is required",
      invalidEmail: "Please enter a valid email",
      invalidPhone: "Please enter a valid phone number",
      consentRequired: "You must agree to continue",
      selectAtLeastOne: "Select at least one option",
      submitting: "Submitting...",
      trustNote: "No spam. We'll only contact you about your inquiry.",
      consentLabel:
        "I agree to be contacted about my inquiry by email or phone.",
      smsConsentLabel:
        "By checking this box, I agree to receive transactional/informational text messages from Kranbos about my inquiry and the services I request. Message frequency will vary. Msg & data rates may apply. Reply HELP for help or STOP to opt out. Consent is not a condition of purchase.",
      legalLinksIntro: "View our",
      privacyLink: "Privacy Policy",
      termsLink: "Terms",
      selectPlaceholder: "Select...",
    },

    /* ── Contact ──────────────────────────────────────────────── */
    contactForm: {
      pageTitle: "Contact Us",
      pageSubtitle:
        "Tell us about your business and where admin work is slowing your team down. We'll get back to you within one business day.",
      submitLabel: "Send Message",
      successTitle: "Message Sent!",
      successMessage:
        "Thanks for reaching out. We'll review your inquiry and get back to you shortly.",
      sideTitle: "How we can help",
      sideBullets: [
        "Free 15-minute call to see if it's a fit",
        "PDF quotes from your price sheets",
        "Search across your files, with source links",
        "Automated reports from your data",
        "Works with the tools you already use",
        "Optional ongoing support",
      ],
      fields: {
        fullName: "Full name",
        email: "Email",
        phone: "Phone",
        companyName: "Company name",
        website: "Website",
        websitePlaceholder: "https://yourcompany.com",
        industry: "Industry",
        industryPlaceholder: "e.g. HVAC, Plumbing, Electrical, Construction",
        helpWith: "What can we help with?",
        helpOptions: {
          ai_operations_audit: "AI Operations Audit ($750)",
          custom_ai_agent: "A custom AI agent for my business",
          field_answers: "Instant answers for my technicians",
          automated_reporting: "Automated owner reports",
          connect_tools: "Connecting the tools we already use",
          managed_support: "Ongoing management of an agent",
          not_sure_yet: "Not sure yet",
        },
        message: "Message",
        messagePlaceholder:
          "Which tools do you use today (QuickBooks, ServiceTitan, Jobber, Google Drive…) and what takes up the most time?",
      },
    },

    /* ── Free Lead Leak Check ─────────────────────────────────── */
    freeLeadLeakCheck: {
      pageTitle: "Free 15-Minute Lead Leak Check",
      pageSubtitle:
        "Book a quick, no-pressure call. We'll review where your leads may be falling through the cracks and give you clear next steps.",
      submitLabel: "Book My Free Check",
      successTitle: "You're Booked!",
      successMessage:
        "We'll reach out within one business day to schedule your 15-minute Lead Leak Check.",
      bookFreeCheck: "Book Your Free Check",
      sideTitle: "What you'll get",
      sideBullets: [
        "15-minute focused review",
        "Identify your biggest lead gaps",
        "Actionable next steps",
        "No sales pressure",
        "Free, no obligation",
      ],
      fields: {
        fullName: "Full name",
        email: "Email",
        phone: "Phone",
        companyName: "Company name",
        website: "Website",
        industry: "Industry",
        industryPlaceholder: "e.g. HVAC, Plumbing, Cleaning",
        leadsSource: "How do most leads come in?",
        leadsSourceOptions: {
          phone_calls: "Phone calls",
          website_forms: "Website forms",
          google_business_profile: "Google Business Profile",
          ads: "Ads",
          referrals: "Referrals",
          social_media: "Social media",
          other: "Other",
        },
        biggestIssue: "Biggest issue right now",
        biggestIssuePlaceholder: "What's the #1 thing you'd like to fix?",
      },
    },

    /* ── Lead Leak Review ────────────────────────────────────── */
    leadLeakReview: {
      pageTitle: "Lead Leak Review",
      pageSubtitle:
        "Tell us how leads reach your business today. After submitting, book a free 15-minute call and we'll walk through it together.",
      submitLabel: "Start Review",
      successTitle: "Intake Complete!",
      successMessage:
        "Your intake has been received. Book a free 15-minute call and we'll walk through where leads may be slipping away.",
      bookReview: "Book a free 15-min call",
      sideTitle: "What we'll cover",
      sideBullets: [
        "Where leads drop off today",
        "Call & form tracking",
        "Follow-up gaps",
        "The CRM and tools you already use",
        "Clear next steps",
      ],
      fields: {
        fullName: "Full name",
        email: "Email",
        phone: "Phone",
        companyName: "Company name",
        website: "Website",
        industry: "Industry",
        industryPlaceholder: "e.g. HVAC, Plumbing, Cleaning",
        serviceArea: "Business location / service area",
        serviceAreaPlaceholder: "e.g. Miami, FL metro area",
        leadsSource: "How do most leads come in?",
        leadsSourceOptions: {
          phone_calls: "Phone calls",
          website_forms: "Website forms",
          google_business_profile: "Google Business Profile",
          ads: "Ads",
          referrals: "Referrals",
          social_media: "Social media",
          other: "Other",
        },
        monthlyVolume: "Approximate monthly lead volume",
        monthlyVolumePlaceholder: "e.g. 50-100 leads/month",
        currentTools: "Current CRM/tools",
        currentToolsPlaceholder: "e.g. ServiceTitan, Housecall Pro, spreadsheets",
        trackMissedCalls: "Do you track missed calls?",
        trackMissedCallsOptions: { yes: "Yes", no: "No", not_sure: "Not sure" },
        followUpEstimates: "Do you follow up on open estimates?",
        followUpEstimatesOptions: {
          yes: "Yes",
          no: "No",
          sometimes: "Sometimes",
          not_applicable: "Not applicable",
        },
        biggestProblem: "Biggest lead/follow-up problem",
        biggestProblemPlaceholder: "What's costing you the most revenue?",
        anythingElse: "Anything we should review before the call?",
        anythingElsePlaceholder: "Optional notes...",
      },
    },

    /* ── Lead Recovery Setup ─────────────────────────────────── */
    leadRecoverySetup: {
      pageTitle: "Lead Recovery Setup",
      pageSubtitle:
        "Tell us about your business so we can prepare a custom setup plan. After submitting, we'll reach out to schedule a free 15-minute call.",
      submitLabel: "Request Setup Plan",
      successTitle: "Request Received!",
      successMessage:
        "We'll review your details and reach out within one business day to schedule a free 15-minute call.",
      bookSetupConsultation: "Book a free 15-min call",
      sideTitle: "What you'll get",
      sideBullets: [
        "Custom automation plan",
        "Missed-call text-back setup",
        "Estimate follow-up sequences",
        "Review request automation",
        "Owner dashboard & weekly reports",
        "Dedicated setup support",
      ],
      fields: {
        fullName: "Full name",
        email: "Email",
        phone: "Phone",
        companyName: "Company name",
        website: "Website",
        industry: "Industry",
        industryPlaceholder: "e.g. HVAC, Plumbing, Cleaning",
        serviceArea: "Business location / service area",
        serviceAreaPlaceholder: "e.g. Miami, FL metro area",
        teamSize: "Team size",
        teamSizePlaceholder: "e.g. 5 technicians + 2 office staff",
        leadsSource: "How do leads come in?",
        leadsSourceOptions: {
          phone_calls: "Phone calls",
          website_forms: "Website forms",
          google_business_profile: "Google Business Profile",
          ads: "Ads",
          referrals: "Referrals",
          social_media: "Social media",
          other: "Other",
        },
        phoneSystem: "Current phone system",
        phoneSystemPlaceholder: "e.g. Vonage, RingCentral, landline",
        currentTools: "Current CRM/tools",
        currentToolsPlaceholder: "e.g. ServiceTitan, Housecall Pro, spreadsheets",
        useSms: "Do you use SMS with customers?",
        useSmsOptions: { yes: "Yes", no: "No", not_sure: "Not sure" },
        automationNeeds: "What needs to be automated?",
        automationNeedsOptions: {
          missed_call_text_back: "Missed-call text-back",
          new_lead_alerts: "New lead alerts",
          estimate_follow_up: "Estimate follow-up",
          review_requests: "Review requests",
          crm_cleanup: "CRM cleanup",
          dashboard_reporting: "Dashboard/reporting",
          weekly_owner_report: "Weekly owner report",
          other: "Other",
        },
        timeline: "Timeline",
        timelineOptions: {
          asap: "ASAP",
          this_month: "This month",
          one_to_three_months: "1\u20133 months",
          just_researching: "Just researching",
        },
        budgetComfort: "Budget comfort",
        budgetComfortOptions: {
          under_3500: "Under $3,500",
          "3500_7500": "$3,500\u2013$7,500",
          "7500_15000": "$7,500\u2013$15,000",
          "15000_plus": "$15,000+",
        },
        messageDetails: "Message/details",
        messageDetailsPlaceholder: "Tell us more about what you need...",
      },
    },

    /* ── Monthly Management ──────────────────────────────────── */
    monthlyManagement: {
      pageTitle: "Monthly Management",
      pageSubtitle:
        "Let us keep your systems running at peak performance. Tell us what you need and we'll build a management plan for you.",
      submitLabel: "Talk About Management",
      successTitle: "Inquiry Received!",
      successMessage:
        "We'll review your needs and get back to you within one business day to schedule a free 15-minute call.",
      bookManagementCall: "Book a free 15-min call",
      sideTitle: "What's included",
      sideBullets: [
        "Automation monitoring",
        "Workflow updates & improvements",
        "Weekly performance reports",
        "CRM cleanup & maintenance",
        "Dashboard upkeep",
        "A clear monthly scope",
      ],
      fields: {
        fullName: "Full name",
        email: "Email",
        phone: "Phone",
        companyName: "Company name",
        website: "Website",
        currentTools: "Current systems/tools",
        currentToolsPlaceholder: "e.g. GoHighLevel, ServiceTitan, custom dashboards",
        managementNeeds: "What needs ongoing management?",
        managementNeedsOptions: {
          monitoring_automations: "Monitoring automations",
          updating_workflows: "Updating workflows",
          weekly_reporting: "Weekly reporting",
          crm_cleanup: "CRM cleanup",
          dashboard_maintenance: "Dashboard maintenance",
          support_requests: "Support requests",
          not_sure_yet: "Not sure yet",
        },
        managingOurSystem: "Are we managing a system we built?",
        managingOurSystemOptions: { yes: "Yes", no: "No", not_sure: "Not sure" },
        supportDetails: "Monthly support needs/details",
        supportDetailsPlaceholder:
          "What kind of ongoing support are you looking for?",
        message: "Message",
        messagePlaceholder: "Anything else we should know? (optional)",
      },
    },
  },

  es: {
    forms: {
      backHome: "Volver al Inicio",
      required: "Este campo es obligatorio",
      invalidEmail: "Ingresa un email v\u00e1lido",
      invalidPhone: "Ingresa un n\u00famero de tel\u00e9fono v\u00e1lido",
      consentRequired: "Debes aceptar para continuar",
      selectAtLeastOne: "Selecciona al menos una opci\u00f3n",
      submitting: "Enviando...",
      trustNote:
        "Sin spam. Solo te contactaremos sobre tu consulta.",
      consentLabel:
        "Acepto ser contactado sobre mi consulta por email o tel\u00e9fono.",
      smsConsentLabel:
        "Al marcar esta casilla, acepto recibir mensajes de texto transaccionales/informativos de Kranbos sobre mi consulta y los servicios que solicito. La frecuencia de los mensajes variar\u00e1. Pueden aplicarse tarifas de mensajes y datos. Responde HELP para obtener ayuda o STOP para darte de baja. El consentimiento no es condici\u00f3n de compra.",
      legalLinksIntro: "Consulta nuestra",
      privacyLink: "Pol\u00edtica de Privacidad",
      termsLink: "T\u00e9rminos",
      selectPlaceholder: "Seleccionar...",
    },

    /* ── Contact ──────────────────────────────────────────────── */
    contactForm: {
      pageTitle: "Cont\u00e1ctanos",
      pageSubtitle:
        "Cu\u00e9ntanos sobre tu negocio y d\u00f3nde el trabajo administrativo frena a tu equipo. Te responderemos en un d\u00eda h\u00e1bil.",
      submitLabel: "Enviar Mensaje",
      successTitle: "\u00a1Mensaje Enviado!",
      successMessage:
        "Gracias por escribirnos. Revisaremos tu consulta y te responderemos pronto.",
      sideTitle: "C\u00f3mo podemos ayudar",
      sideBullets: [
        "Llamada gratis de 15 minutos para ver si encajamos",
        "Cotizaciones PDF desde tus listas de precios",
        "B\u00fasqueda en tus archivos, con enlace a la fuente",
        "Reportes autom\u00e1ticos desde tus datos",
        "Funciona con las herramientas que ya usas",
        "Soporte continuo opcional",
      ],
      fields: {
        fullName: "Nombre completo",
        email: "Email",
        phone: "Tel\u00e9fono",
        companyName: "Nombre de la empresa",
        website: "Sitio web",
        websitePlaceholder: "https://tuempresa.com",
        industry: "Industria",
        industryPlaceholder: "ej. HVAC, Plomer\u00eda, Electricidad, Construcci\u00f3n",
        helpWith: "\u00bfEn qu\u00e9 podemos ayudarte?",
        helpOptions: {
          ai_operations_audit: "Auditor\u00eda de Operaciones con IA ($750)",
          custom_ai_agent: "Un agente de IA a medida para mi negocio",
          field_answers: "Respuestas al instante para mis t\u00e9cnicos",
          automated_reporting: "Reportes autom\u00e1ticos para el due\u00f1o",
          connect_tools: "Conectar las herramientas que ya usamos",
          managed_support: "Gesti\u00f3n continua de un agente",
          not_sure_yet: "No estoy seguro a\u00fan",
        },
        message: "Mensaje",
        messagePlaceholder:
          "\u00bfQu\u00e9 herramientas usas hoy (QuickBooks, ServiceTitan, Jobber, Google Drive\u2026) y qu\u00e9 te quita m\u00e1s tiempo?",
      },
    },

    /* ── Free Lead Leak Check ─────────────────────────────────── */
    freeLeadLeakCheck: {
      pageTitle: "Chequeo Gratis de Fugas de Leads en 15 Minutos",
      pageSubtitle:
        "Agenda una llamada r\u00e1pida sin presi\u00f3n. Revisaremos d\u00f3nde tus leads pueden estar cay\u00e9ndose y te daremos pasos claros.",
      submitLabel: "Agendar Mi Chequeo Gratis",
      successTitle: "\u00a1Agendado!",
      successMessage:
        "Nos comunicaremos dentro de un d\u00eda h\u00e1bil para programar tu Chequeo de Fugas de Leads de 15 minutos.",
      bookFreeCheck: "Agenda Tu Chequeo Gratis",
      sideTitle: "Lo que obtendr\u00e1s",
      sideBullets: [
        "Revisi\u00f3n enfocada de 15 minutos",
        "Identificar tus mayores brechas de leads",
        "Pasos accionables",
        "Sin presi\u00f3n de venta",
        "Gratis y sin compromiso",
      ],
      fields: {
        fullName: "Nombre completo",
        email: "Email",
        phone: "Tel\u00e9fono",
        companyName: "Nombre de la empresa",
        website: "Sitio web",
        industry: "Industria",
        industryPlaceholder: "ej. HVAC, Plomer\u00eda, Limpieza",
        leadsSource: "\u00bfC\u00f3mo llegan la mayor\u00eda de los leads?",
        leadsSourceOptions: {
          phone_calls: "Llamadas telef\u00f3nicas",
          website_forms: "Formularios web",
          google_business_profile: "Perfil de Google Business",
          ads: "Anuncios",
          referrals: "Referidos",
          social_media: "Redes sociales",
          other: "Otro",
        },
        biggestIssue: "Mayor problema ahora mismo",
        biggestIssuePlaceholder: "\u00bfQu\u00e9 es lo #1 que te gustar\u00eda resolver?",
      },
    },

    /* ── Lead Leak Review ────────────────────────────────────── */
    leadLeakReview: {
      pageTitle: "Revisi\u00f3n de Fugas de Leads",
      pageSubtitle:
        "Cu\u00e9ntanos c\u00f3mo te llegan los leads hoy. Despu\u00e9s de enviar, agenda una llamada gratis de 15 minutos y lo revisamos juntos.",
      submitLabel: "Iniciar Revisi\u00f3n",
      successTitle: "\u00a1Formulario Completado!",
      successMessage:
        "Hemos recibido tu formulario. Agenda una llamada gratis de 15 minutos y revisaremos d\u00f3nde se pueden estar escapando tus leads.",
      bookReview: "Agenda una llamada gratis de 15 min",
      sideTitle: "Qu\u00e9 revisaremos",
      sideBullets: [
        "D\u00f3nde se pierden los leads hoy",
        "Seguimiento de llamadas y formularios",
        "Brechas en el seguimiento",
        "El CRM y las herramientas que ya usas",
        "Pr\u00f3ximos pasos claros",
      ],
      fields: {
        fullName: "Nombre completo",
        email: "Email",
        phone: "Tel\u00e9fono",
        companyName: "Nombre de la empresa",
        website: "Sitio web",
        industry: "Industria",
        industryPlaceholder: "ej. HVAC, Plomer\u00eda, Limpieza",
        serviceArea: "Ubicaci\u00f3n / \u00e1rea de servicio",
        serviceAreaPlaceholder: "ej. \u00c1rea metropolitana de Miami, FL",
        leadsSource: "\u00bfC\u00f3mo llegan la mayor\u00eda de los leads?",
        leadsSourceOptions: {
          phone_calls: "Llamadas telef\u00f3nicas",
          website_forms: "Formularios web",
          google_business_profile: "Perfil de Google Business",
          ads: "Anuncios",
          referrals: "Referidos",
          social_media: "Redes sociales",
          other: "Otro",
        },
        monthlyVolume: "Volumen mensual aproximado de leads",
        monthlyVolumePlaceholder: "ej. 50-100 leads/mes",
        currentTools: "CRM/herramientas actuales",
        currentToolsPlaceholder: "ej. ServiceTitan, Housecall Pro, hojas de c\u00e1lculo",
        trackMissedCalls: "\u00bfRastrean llamadas perdidas?",
        trackMissedCallsOptions: {
          yes: "S\u00ed",
          no: "No",
          not_sure: "No estoy seguro",
        },
        followUpEstimates: "\u00bfDan seguimiento a presupuestos abiertos?",
        followUpEstimatesOptions: {
          yes: "S\u00ed",
          no: "No",
          sometimes: "A veces",
          not_applicable: "No aplica",
        },
        biggestProblem: "Mayor problema de leads/seguimiento",
        biggestProblemPlaceholder: "\u00bfQu\u00e9 te est\u00e1 costando m\u00e1s ingresos?",
        anythingElse: "\u00bfAlgo que debamos revisar antes de la llamada?",
        anythingElsePlaceholder: "Notas opcionales...",
      },
    },

    /* ── Lead Recovery Setup ─────────────────────────────────── */
    leadRecoverySetup: {
      pageTitle: "Instalaci\u00f3n de Recuperaci\u00f3n de Leads",
      pageSubtitle:
        "Cu\u00e9ntanos sobre tu negocio para preparar un plan personalizado. Despu\u00e9s de enviar, nos pondremos en contacto para agendar una llamada gratis de 15 minutos.",
      submitLabel: "Solicitar Plan de Instalaci\u00f3n",
      successTitle: "\u00a1Solicitud Recibida!",
      successMessage:
        "Revisaremos tus datos y nos comunicaremos en un d\u00eda h\u00e1bil para agendar una llamada gratis de 15 minutos.",
      bookSetupConsultation: "Agenda una llamada gratis de 15 min",
      sideTitle: "Lo que obtendr\u00e1s",
      sideBullets: [
        "Plan de automatizaci\u00f3n personalizado",
        "Configuraci\u00f3n de respuesta a llamadas perdidas",
        "Secuencias de seguimiento de presupuestos",
        "Automatizaci\u00f3n de solicitud de rese\u00f1as",
        "Panel del due\u00f1o y reportes semanales",
        "Soporte dedicado de instalaci\u00f3n",
      ],
      fields: {
        fullName: "Nombre completo",
        email: "Email",
        phone: "Tel\u00e9fono",
        companyName: "Nombre de la empresa",
        website: "Sitio web",
        industry: "Industria",
        industryPlaceholder: "ej. HVAC, Plomer\u00eda, Limpieza",
        serviceArea: "Ubicaci\u00f3n / \u00e1rea de servicio",
        serviceAreaPlaceholder: "ej. \u00c1rea metropolitana de Miami, FL",
        teamSize: "Tama\u00f1o del equipo",
        teamSizePlaceholder: "ej. 5 t\u00e9cnicos + 2 personal de oficina",
        leadsSource: "\u00bfC\u00f3mo llegan los leads?",
        leadsSourceOptions: {
          phone_calls: "Llamadas telef\u00f3nicas",
          website_forms: "Formularios web",
          google_business_profile: "Perfil de Google Business",
          ads: "Anuncios",
          referrals: "Referidos",
          social_media: "Redes sociales",
          other: "Otro",
        },
        phoneSystem: "Sistema telef\u00f3nico actual",
        phoneSystemPlaceholder: "ej. Vonage, RingCentral, l\u00ednea fija",
        currentTools: "CRM/herramientas actuales",
        currentToolsPlaceholder: "ej. ServiceTitan, Housecall Pro, hojas de c\u00e1lculo",
        useSms: "\u00bfUsan SMS con clientes?",
        useSmsOptions: { yes: "S\u00ed", no: "No", not_sure: "No estoy seguro" },
        automationNeeds: "\u00bfQu\u00e9 necesita automatizarse?",
        automationNeedsOptions: {
          missed_call_text_back: "Respuesta a llamadas perdidas",
          new_lead_alerts: "Alertas de nuevos leads",
          estimate_follow_up: "Seguimiento de presupuestos",
          review_requests: "Solicitudes de rese\u00f1as",
          crm_cleanup: "Limpieza de CRM",
          dashboard_reporting: "Paneles/reportes",
          weekly_owner_report: "Reporte semanal del due\u00f1o",
          other: "Otro",
        },
        timeline: "Cronograma",
        timelineOptions: {
          asap: "Lo antes posible",
          this_month: "Este mes",
          one_to_three_months: "1\u20133 meses",
          just_researching: "Solo investigando",
        },
        budgetComfort: "Comodidad de presupuesto",
        budgetComfortOptions: {
          under_3500: "Menos de $3,500",
          "3500_7500": "$3,500\u2013$7,500",
          "7500_15000": "$7,500\u2013$15,000",
          "15000_plus": "$15,000+",
        },
        messageDetails: "Mensaje/detalles",
        messageDetailsPlaceholder: "Cu\u00e9ntanos m\u00e1s sobre lo que necesitas...",
      },
    },

    /* ── Monthly Management ──────────────────────────────────── */
    monthlyManagement: {
      pageTitle: "Gesti\u00f3n Mensual",
      pageSubtitle:
        "Deja que mantengamos tus sistemas funcionando al m\u00e1ximo. Cu\u00e9ntanos qu\u00e9 necesitas y construiremos un plan de gesti\u00f3n para ti.",
      submitLabel: "Hablar Sobre Gesti\u00f3n",
      successTitle: "\u00a1Consulta Recibida!",
      successMessage:
        "Revisaremos tus necesidades y te responderemos en un d\u00eda h\u00e1bil para agendar una llamada gratis de 15 minutos.",
      bookManagementCall: "Agenda una llamada gratis de 15 min",
      sideTitle: "Qu\u00e9 incluye",
      sideBullets: [
        "Monitoreo de automatizaciones",
        "Actualizaciones y mejoras de flujos",
        "Reportes semanales de rendimiento",
        "Limpieza y mantenimiento de CRM",
        "Mantenimiento de paneles",
        "Un alcance mensual claro",
      ],
      fields: {
        fullName: "Nombre completo",
        email: "Email",
        phone: "Tel\u00e9fono",
        companyName: "Nombre de la empresa",
        website: "Sitio web",
        currentTools: "Sistemas/herramientas actuales",
        currentToolsPlaceholder: "ej. GoHighLevel, ServiceTitan, paneles personalizados",
        managementNeeds: "\u00bfQu\u00e9 necesita gesti\u00f3n continua?",
        managementNeedsOptions: {
          monitoring_automations: "Monitoreo de automatizaciones",
          updating_workflows: "Actualizaci\u00f3n de flujos",
          weekly_reporting: "Reportes semanales",
          crm_cleanup: "Limpieza de CRM",
          dashboard_maintenance: "Mantenimiento de paneles",
          support_requests: "Solicitudes de soporte",
          not_sure_yet: "No estoy seguro a\u00fan",
        },
        managingOurSystem: "\u00bfEstamos gestionando un sistema que construimos?",
        managingOurSystemOptions: {
          yes: "S\u00ed",
          no: "No",
          not_sure: "No estoy seguro",
        },
        supportDetails: "Necesidades/detalles de soporte mensual",
        supportDetailsPlaceholder:
          "\u00bfQu\u00e9 tipo de soporte continuo buscas?",
        message: "Mensaje",
        messagePlaceholder: "\u00bfAlgo m\u00e1s que debamos saber? (opcional)",
      },
    },
  },
};
